/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to get access to router context.
 */

/*
 * Used to style components
 */

/**
 * Used for the basic page layout.
 */
import {
    GroupedField,
    IFormLabel,
    IFormValidationError,
    IInstitution,
    ILookup,
    Text,
    ToggleButtonGroup,
    ValidationErrorType
} from '@ngt/opms';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to type patient state.
 */
import { IFormValidate, useScopedField } from '@ngt/forms-core';
import { useCallback } from 'react';
import { ConsentTypeEnum, StartConsent } from '../../../api/dtos';
import { INVESTIGATOR_LOOKUP_OTHER } from '../../../hooks/data/useInvestigators';
import InvestigatorSelector from './InvestigatorSelector';


export const consentFieldsLookups: ILookup[] = [
    {
        propertyName: 'consentEvent.consentTypeId',
        items: [
            {
                id: ConsentTypeEnum.Remote,
                value: 'Yes'
            },
            {
                id: ConsentTypeEnum.Standard,
                value: 'No'
            }
        ]
    }
];

export const consentFieldsLabels: IFormLabel[] = [
    {
        propertyPath: 'consentEvent.consentTypeId',
        label: 'Will consent forms be signed using eConsent?',
        detailedLabel: 'Will consent forms be signed using eConsent?',
    },
    {
        propertyPath: 'firstName',
        label: 'Participant\'s First Name',
        detailedLabel: 'Participant\'s First Name',
    },
    {
        propertyPath: 'lastName',
        label: 'Participant\'s Last Name',
        detailedLabel: 'Participant\'s Last Name',
    },
    {
        propertyPath: 'consentEvent.investigator',
        label: 'Investigator',
        detailedLabel: 'Investigator'
    },
    {
        propertyPath: 'consentEvent.investigatorSpecify',
        label: 'Other - specify',
        detailedLabel: 'Other - specify'
    }
];

export const useConsentFieldsValidation = (institution?: IInstitution | null) => {
    
    const consentValidate: IFormValidate<StartConsent, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.consentEvent?.consentTypeId === undefined || 
            formState.value?.consentEvent?.consentTypeId === null) {
            errors['consentEvent.consentTypeId'] = [
                {
                    property: 'consentEvent.consentTypeId',
                    code: 'C-001',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        if (formState.value?.consentEvent?.consentTypeId === ConsentTypeEnum.Remote && 
            !institution?.eConsentActivationDate) {
            errors['consentEvent.consentTypeId'] = [
                {
                    property: 'consentEvent.consentTypeId',
                    code: 'C-002',
                    message: 'Site eConsent is not activated.',
                    detailedMessage: 'Site eConsent is not activated.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        if ((formState.value?.firstName === undefined || formState.value?.firstName === null) &&
            formState.value?.consentEvent?.consentTypeId === ConsentTypeEnum.Remote) {
            errors['firstName'] = [
                {
                    property: 'firstName',
                    code: 'C-003',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }
        if ((formState.value?.lastName === undefined || formState.value?.lastName === null) &&
            formState.value?.consentEvent?.consentTypeId === ConsentTypeEnum.Remote) {
            errors['lastName'] = [
                {
                    property: 'lastName',
                    code: 'C-004',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }
        if ((formState.value?.consentEvent?.investigator === undefined || 
            formState.value?.consentEvent?.investigator === null) &&
            formState.value?.consentEvent?.consentTypeId === ConsentTypeEnum.Remote) {
            errors['consentEvent.investigator'] = [
                {
                    property: 'consentEvent.investigator',
                    code: 'C-005',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }
        if (formState.value?.consentEvent?.investigator === INVESTIGATOR_LOOKUP_OTHER && 
            formState.value?.consentEvent?.investigatorSpecify == null) {
            errors['consentEvent.investigatorSpecify'] = [
                {
                    property: 'consentEvent.investigatorSpecify',
                    code: 'C-006',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        if (formState.value?.consentEvent?.consentTypeId === ConsentTypeEnum.Remote && 
            formState.value?.consentEvent?.investigator === INVESTIGATOR_LOOKUP_OTHER) {
            errors['consentEvent.investigator'] = [
                {
                    property: 'consentEvent.investigator',
                    code: 'C-007',
                    message: 'This field is required to have a valid investigator.',
                    detailedMessage: 'This field is required to have a valid investigator.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        return errors;
    }, [institution])

    return consentValidate;
}

export interface IConsentFieldsProps {
    institutionCode?: string
}

const ConsentFields: React.FunctionComponent<IConsentFieldsProps> = ({
    institutionCode
}) => {

    const {
        state: {
            value: consentTypeId
        }
    } = useScopedField<number>(`consentEvent.consentTypeId`, { value: true }, false, false);

    return <>
        <GroupedField
            propertyPath="consentEvent.consentTypeId"
            component={ToggleButtonGroup}
            FieldGridProps={{
                size: {
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 6,
                    xl: 6
                }
            }}
        />

        {
            consentTypeId === ConsentTypeEnum.Remote && <>
                <GroupedField
                    propertyPath="firstName"
                    component={Text}
                    FieldGridProps={{
                        size: {
                            xs: 12,
                            sm: 6,
                            md: 6,
                            lg: 6,
                            xl: 6
                        }
                    }}
                />

                <GroupedField
                    propertyPath="lastName"
                    component={Text}
                    FieldGridProps={{
                        size: {
                            xs: 12,
                            sm: 6,
                            md: 6,
                            lg: 6,
                            xl: 6
                        }
                    }}
                />
                <InvestigatorSelector 
                    propertyPath="consentEvent.investigator"
                    //otherPropertyPath="consentEvent.investigatorSpecify"
                    institutionCode={institutionCode}
                    hideOther
                />  
            </>
        }
    </>
}

export default ConsentFields;